/**
 * 작성자 : 홍선영
 * 날짜 : 2024.01.22
 * 기능 : 근로자 출입 현황 대시보드 컴포넌트 (구역별 상황판)
 */

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { apiGet } from '../../services/_common';
import { PhotoTableListStyle } from '../../assets/styles/PhotoTableList';
import { formatMMDDHHMMSS } from '../../utils/formatDate';
import IssueGuide from '../IssueGuide';
import { ERROR } from '../../_constants';
import SkeletonTable from '../SkeletonTable';

interface Iprops {
  name: string;
  userInfo: any;
  tatCd: string;
  refetchInterval: number;
}

interface IworkerAttend {
  aImg?: any;
  wCd: string;
  wName: string;
  sjCd: string;
  sjName: string;
  wPrejobtype: string;
  wPrejobtypeName: string;
  wJobtype: string;
  wJobtypeName: string;
  aDate: string;
}

export const WorkerAttend = ({ name, userInfo, tatCd, refetchInterval }: Iprops) => {
  const { hCd, sCd } = userInfo;
  const { t } = useTranslation();

  // 근로자 출입현황 조회
  const { data, isLoading, isError } = useQuery(['tAreaWorkerAttendGet', hCd, sCd, tatCd], () => fetchData(), {
    enabled: !!hCd && !!sCd && sCd !== '00000' && !!tatCd,
    refetchInterval,
  });

  const fetchData = async () => {
    try {
      const req = { hCd, sCd, tatCd };
      const res = await apiGet({ path: `/tarea/board/attend`, req });
      const tareaList = res.data.data?.tareaList || [];
      return tareaList;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  const loading = isLoading || !data;
  if (isError) return <IssueGuide text={ERROR} type='error' />;

  return (
    <PhotoTableListStyle>
      <div className='widget-header safety'>
        <div className='widget-title flex-between'>
          <span>
            {t(name)}
            <span className='badge'>{data?.length ?? 0}</span>
          </span>
        </div>
      </div>
      {loading ? (
        <SkeletonTable rows={14} />
      ) : (
        <div className='widget-body photoTable'>
          <div className='sliderWrapper'>
            <div className='text-container active' style={data.length > 14 ? { animation: `slide ${data.length * 2}s linear infinite` } : undefined}>
              <WorkerList workerList={data} />
            </div>
          </div>
        </div>
      )}
    </PhotoTableListStyle>
  );
};

const WorkerList = ({ workerList }: { workerList: IworkerAttend[] }) => {
  return (
    <div>
      {workerList.map((worker: IworkerAttend) => (
        <div key={`${worker.wCd}`} className='rowWrapper flex-basic'>
          <div className='flex-basic'>
            <div className='imgWrapper'>{worker.aImg ? <img src={worker.aImg} alt='worker_img' /> : <span className='material-symbols-rounded'>person </span>}</div>
            <div className='flex-col'>
              <div className='flex-basic flex-between'>
                <div className='ellipsis' style={{ paddingRight: '0.5rem' }}>
                  {worker.wName}
                </div>
                <div className='buttonsWrapper flex-basic'>
                  <button type='button' className='gray'>
                    <span>{formatMMDDHHMMSS(worker.aDate)}</span>
                  </button>
                </div>
              </div>
              <div className='flex-basic flex-between'>
                <div style={{ paddingRight: '0.5rem' }}>
                  {worker.sjName} {worker.wPrejobtypeName}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};
